import { TbMail } from 'react-icons/tb'

import { RichTextInput } from '@/Ship/Components/RichText/RichTextInput'
import { renderRichTextView } from '@/Ship/Components/RichText/RichTextView'
import { LETTER_PROVIDER_OPTIONS } from '@/Ship/Contracts/LetterProvider'
import { attachUploadedFiles } from '@/Ship/Helpers/attachUploadedFiles'

import { TopMenu } from '../Components/TopMenu'
import { serviceFields } from '../Contracts/DictionaryFields'
import {
    LETTER_TEMPLATE_FILE_EXTENSIONS,
    LETTER_TEMPLATE_FILE_MAX_SIZE_MB,
    LETTER_TEMPLATE_FILE_UPLOAD_URL,
    LETTER_TEMPLATE_LABELS as L,
    LETTER_TEMPLATE_REST_URL,
    type LetterTemplateBinding,
    type LetterTemplateFormValues,
} from '../Contracts/LetterTemplate'
import { CheckDictionaryPageAccessTask } from '../Tasks/CheckDictionaryPageAccessTask'
import { ListLetterTemplateFormTemplate } from '../Templates/ListLetterTemplateFormTemplate'

import type { IResourceConfig } from '@/Core/Contracts/UI'
import type { IFormEventContext } from '@/Core/Contracts/UI/Form'

/** Мінімум поля й форми, з яким працює `depFn` ядра. */
interface BindingDepFnArgs {
    formContext: {
        contextKey?: string
        values?: LetterTemplateFormValues
        setValue?: (path: string, value: unknown) => void
    }
}

/**
 * Прив'язки показуються лише для свого провайдера (R-06): клієнти — для
 * `client`, перевізники — для `carrier`. Зміна провайдера очищує прихований
 * селект, як у легасі; бек відкине «чужі» прив'язки й сам.
 */
const onlyForProvider =
    (provider: 'client' | 'carrier', field: 'clients' | 'carriers') =>
    ({ formContext }: BindingDepFnArgs): boolean | undefined => {
        if (formContext.contextKey === 'filterFormContext') return undefined

        if (formContext.values?.provider === provider) return undefined

        const current = formContext.values?.[field]
        if (Array.isArray(current) && current.length > 0) formContext.setValue?.(field, [])

        return false
    }

/**
 * «Офіційний лист заявки» — лише для провайдера «Заявки» (`SPEC-025` §3.2):
 * при іншому провайдері перемикач ховається й скидається, бек на чужому
 * провайдері відповідає 422.
 */
const onlyForApplications = ({ formContext }: BindingDepFnArgs): boolean | undefined => {
    if (formContext.contextKey === 'filterFormContext') return undefined

    if (formContext.values?.provider === 'application') return undefined

    if (formContext.values?.['isOfficialApplication'] === true) formContext.setValue?.('isOfficialApplication', false)

    return false
}

/** Назви прив'язаних через кому — колонка «Клієнти / перевізники». */
const bindingTitles = (value: unknown): string[] =>
    Array.isArray(value)
        ? (value as LetterTemplateBinding[])
              .map((item) => (typeof item?.title === 'string' ? item.title : ''))
              .filter((title) => title !== '')
        : []

/**
 * Поля, яких бек від форми не приймає: колонку `bindings` збирає таблиця,
 * службові ставить сервер. Не шлемо їх, щоб тіло запиту не обіцяло зміни,
 * якої не буде.
 */
const READ_ONLY_KEYS = ['bindings', 'id', 'isRemoved', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'] as const

const prepareLetterTemplatePayload = ({ values }: IFormEventContext): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])
}

/**
 * Довідник «Шаблони листів» — 21-й на сторінці «Довідники» (`SPEC-013` §9.1,
 * `SPEC-007` §4 № 18), група «Реквізити й документи».
 *
 * - «Провайдер» — статичні опції `LetterProvider.ts` (D-08), без ресурсу
 *   провайдерів;
 * - «Клієнт» / «Перевізник» — справжні зв'язки (M2M), а не рядок `objectIds`
 *   з `@id|title|id` (Л-11); показуються лише для свого провайдера;
 * - «Офіційний лист заявки» — лише для провайдера «Заявки» (ДС-01: у легасі
 *   ознакою було `sorting = 10000`); відправка такого листа ставить заявці
 *   «лист відправлено»;
 * - «Шаблон» — Ship `RichTextInput` з пресетом панелі «letter» (ADR 036),
 *   перегляд — `RichTextView`; бек санітизує HTML і не зберігає шаблон із
 *   невідомою змінною `${…}` (422 на полі);
 * - під редактором — перелік змінних провайдера (`LetterTemplateVariables`);
 * - «Вкладення» — двокроково (G-05): ядровий `FileInput` вантажить файл
 *   сиротою на `POST /services/letter_template_files`, збереження шаблону
 *   прикріплює його через `fileUuids` (`attachUploadedFiles`). Тож вкладення
 *   додаються й під час створення — у легасі лише після першого збереження.
 *   Посилання на файл — підписане, з терміном дії (ADR 042).
 *
 * Сторінку бачить лише адміністратор (`pageConfig.accessFn`), читати шаблони
 * для кнопок листа може кожен (S8b).
 */
export const ListLetterTemplateResource: IResourceConfig = {
    title: 'Шаблони листів',
    fields: {
        provider: {
            label: L.provider,
            type: 'select',
            options: LETTER_PROVIDER_OPTIONS,
            isRequired: true,
        },
        clients: {
            label: L.clients,
            type: 'select',
            resource: 'Crm.Client',
            isMultiple: true,
            depFn: onlyForProvider('client', 'clients'),
        },
        carriers: {
            label: L.carriers,
            type: 'select',
            resource: 'Crm.Carrier',
            isMultiple: true,
            depFn: onlyForProvider('carrier', 'carriers'),
        },
        // Лише колонка таблиці: назви прив'язаних клієнтів чи перевізників
        // (Q13.11 (а)); у формі її немає — там два окремі селекти
        bindings: {
            label: L.bindings,
            type: 'string',
            isEditable: false,
        },
        isDefault: {
            label: L.isDefault,
            type: 'switch',
        },
        isOfficialApplication: {
            label: L.isOfficialApplication,
            type: 'switch',
            depFn: onlyForApplications,
        },
        title: {
            label: L.title,
            type: 'string',
            isRequired: true,
            inputProps: { maxLength: 150 },
        },
        subject: {
            label: L.subject,
            type: 'string',
            inputProps: { maxLength: 255 },
        },
        toWhom: {
            label: L.toWhom,
            type: 'string',
            inputProps: { maxLength: 255, placeholder: '${emails}' },
        },
        cc: {
            label: L.cc,
            type: 'string',
            inputProps: { maxLength: 255 },
        },
        bcc: {
            label: L.bcc,
            type: 'string',
            inputProps: { maxLength: 255 },
        },
        body: {
            label: L.body,
            input: RichTextInput,
            view: renderRichTextView,
            inputProps: { placeholder: 'Текст листа…', toolbar: 'letter' },
        },
        attachments: {
            label: L.attachments,
            type: 'file',
            isMultiple: true,
            uploadUrl: LETTER_TEMPLATE_FILE_UPLOAD_URL,
            uploadAccept: LETTER_TEMPLATE_FILE_EXTENSIONS,
            uploadMaxSizeMb: LETTER_TEMPLATE_FILE_MAX_SIZE_MB,
            showViewIcon: false,
            showEditIcon: false,
        },
        sorting: {
            label: L.sorting,
            type: 'number',
            defaultValue: 500,
        },
        ...serviceFields(),
    },
    form: {
        template: ListLetterTemplateFormTemplate,
        eventHandlers: {
            // Крок 2: нові вкладення — у `fileUuids`, поле `attachments` — геть із тіла
            onBeforeTransformSendSaveRequest: {
                letterTemplateFileUuids: attachUploadedFiles('attachments'),
            },
            onBeforeSendSaveRequest: {
                LetterTemplatePayload: prepareLetterTemplatePayload,
            },
        },
    },
    // Колонки легасі (`ListLetterTemplatePage.jsx:70-85`)
    table: {
        showColumns: ['provider', 'bindings', 'title', 'subject', 'toWhom', 'isDefault', 'sorting'],
        dataSourceNoTransform: ['bindings'],
        eventHandlers: {
            onBuildDataSourceItem: {
                LetterTemplateBindings: ({ item }: { item: Record<string, unknown> }): void => {
                    const original = (item['_original'] ?? item) as Record<string, unknown>
                    const titles = [...bindingTitles(original['clients']), ...bindingTitles(original['carriers'])]

                    item['bindings'] = titles.length > 0 ? titles.join(', ') : '—'
                },
            },
        },
    },
    // Фільтри легасі: Ід · Тема · Провайдер (`ListLetterTemplatePage.jsx:63-65`)
    filter: {
        includeFields: ['id', 'subject', 'provider'],
    },
    drawer: {
        width: 800,
    },
    places: {
        top: <TopMenu current="letter-templates" />,
    },
    restUrl: LETTER_TEMPLATE_REST_URL,
    pageUrl: '/settings/letter-templates',
    pageConfig: {
        title: 'Шаблони листів',
        icon: <TbMail />,
        accessFn: CheckDictionaryPageAccessTask,
    },
    settings: {
        searchFields: ['id', 'title'],
    },
}
